import {
  AlarmClockOff,
  AlertTriangle,
  CalendarCheck,
  CheckCircle2,
  UserRoundPlus,
  UsersRound,
} from 'lucide-react';
import { type CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAuthStore } from '@/app/store/auth.store';
import { useAuthCanRegisterPatient } from '@/app/store/auth.store';
import { useHomeDashboard } from '@/shared/hooks/use-home-dashboard';

import { Button } from '@/components/ui/Button';
import { DetailPanel } from './components/DetailPanel';
import { PatientListModal } from './components/PatientListModal';
import { SectionCard } from './components/SectionCard';
import type { SectionId } from './constants';
import { SectionId as Section } from './constants';
import { formatDateText, formatNowTime } from './utils';

export function MedicalHomePage() {
  const { t } = useTranslation(['medical', 'common']);
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useHomeDashboard();
  const canRegister = useAuthCanRegisterPatient();
  const name = useAuthStore((s) => s.name);

  // Deep-link entry (PRD 08 §3.2 — PRO_DELAY 알림 클릭 → /home?section=delay): open that section on mount.
  const [searchParams, setSearchParams] = useSearchParams();
  const sectionParam = searchParams.get('section');
  const initialSection: SectionId = (Object.values(Section) as string[]).includes(
    sectionParam ?? '',
  )
    ? (sectionParam as SectionId)
    : Section.R;
  const [selectedSection, setSelectedSection] = useState<SectionId>(initialSection);
  const [showPatientList, setShowPatientList] = useState(false);

  // Deep-link entry (PRD 02 §3.4 · §3.5.1 / SHCT-462): /home?openViewAll=1 auto-opens the
  // full-patient-list modal, then strips the param so a refresh doesn't reopen it.
  useEffect(() => {
    if (searchParams.get('openViewAll') === '1') {
      setShowPatientList(true);
      const next = new URLSearchParams(searchParams);
      next.delete('openViewAll');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const greeting = useMemo(() => {
    return name
      ? t('medical:home.greeting', { name })
      : t('medical:home.greetingFallback');
  }, [t, name]);

  const dateText = useMemo(() => {
    if (!data) return '—';
    const datePart = formatDateText(data.todayStr);
    return `${datePart} · ${formatNowTime()} 갱신`;
  }, [data]);

  const handleRegister = useCallback(() => {
    navigate('/patients?openReg=1');
  }, [navigate]);

  const handleViewAll = useCallback(() => {
    setShowPatientList(true);
  }, []);

  const handleSectionClick = useCallback((id: SectionId) => {
    setSelectedSection(id);
  }, []);

  // PRD §5: loading → skeleton; §5/§8.1: error → message + retry.
  if (isLoading) {
    return <HomeSkeleton />;
  }
  if (isError || !data) {
    return (
      <div className="content__body">
        <div className="empty-state" style={{ padding: 48 }}>
          <span className="empty-state__desc">{t('medical:home.error.load')}</span>
          <div className="empty-state__action">
            <Button variant="secondary" onClick={() => refetch()}>
              {t('medical:home.error.retry')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* ── Header ── */}
      <div className="content__header home__header content__header--with-action">
        <div className="home__header-titles">
          <h1 className="content__title">{greeting}</h1>
          <div className="home__date">{dateText}</div>
        </div>
        <div className="home__actions">
          <Button variant="secondary" className="home__action" onClick={handleViewAll}>
            <UsersRound size={20} />
            <span>{t('medical:home.actions.viewAllPatients')}</span>
          </Button>
          <Button
            variant="primary"
            className="home__action home__action--primary"
            onClick={handleRegister}
            disabled={!canRegister}
          >
            <UserRoundPlus size={20} />
            <span>{t('medical:home.actions.registerPatient')}</span>
          </Button>
        </div>
      </div>

      {/* ── Body: 4-section cards + detail panel ── */}
      <div className="content__body" style={{ padding: '0 24px 24px' }}>
        <div className="today-layout">
          <div className="today-sections">
            <SectionCard
              id="r" icon={AlertTriangle}
              title={t('medical:home.sections.atRisk.title')}
              count={data.dangerR.length}
              unit={t('medical:home.sections.atRisk.unit')}
              caption={t('medical:home.sections.atRisk.cardCaption', {
                threshold: data.R_DANGER_THRESHOLD,
              })}
              variant="r"
              isActive={selectedSection === Section.R}
              onClick={handleSectionClick}
            />
            <SectionCard
              id="severity" icon={CheckCircle2}
              title={t('medical:home.sections.todayPro.title')}
              count={data.severePatientCount}
              unit={t('medical:home.sections.todayPro.unit')}
              caption={t('medical:home.sections.todayPro.caption')}
              variant="severity"
              isActive={selectedSection === Section.SEVERITY}
              onClick={handleSectionClick}
            />
            <SectionCard
              id="delay" icon={AlarmClockOff}
              title={t('medical:home.sections.responseDelay.title')}
              count={data.delayed.length}
              unit={t('medical:home.sections.responseDelay.unit')}
              caption={t('medical:home.sections.responseDelay.caption')}
              variant="delay"
              isActive={selectedSection === Section.DELAY}
              onClick={handleSectionClick}
            />
            <SectionCard
              id="schedule" icon={CalendarCheck}
              title={t('medical:home.sections.schedule.title')}
              count={data.todaySchedule.length}
              unit={t('medical:home.sections.schedule.unit')}
              prefix={t('medical:home.sections.schedule.todayPrefix')}
              caption={t('medical:home.sections.schedule.caption')}
              variant="schedule"
              isActive={selectedSection === Section.SCHEDULE}
              onClick={handleSectionClick}
            />
          </div>

          <div className={`card today-card today-card--right today-card--theme-${selectedSection}`}>
            <DetailPanel section={selectedSection} data={data} t={t} navigate={navigate} />
          </div>
        </div>
      </div>

      {/* ── Patient List Modal ── */}
      {showPatientList && (
        <PatientListModal
          t={t}
          onClose={() => setShowPatientList(false)}
          navigate={navigate}
        />
      )}
    </>
  );
}

/** Loading skeleton for the Home dashboard (PRD §5). Reuses the notif-skeleton pulse keyframe. */
function HomeSkeleton() {
  const block = (h: number, w: string | number = '100%'): CSSProperties => ({
    height: h,
    width: w,
    borderRadius: 6,
    background: '#e5e7eb',
    animation: 'notif-skeleton-pulse 1.4s ease-in-out infinite',
  });
  return (
    <div className="content__body" style={{ padding: '0 24px 24px' }} aria-busy="true">
      <div className="today-layout">
        <div className="today-sections">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="today-section" style={{ pointerEvents: 'none' }}>
              <span className="today-section__main" style={{ display: 'grid', gap: 10 }}>
                <span style={block(16, '40%')} />
                <span style={block(28, '30%')} />
                <span style={block(12, '60%')} />
              </span>
            </div>
          ))}
        </div>
        <div className="card today-card today-card--right">
          <div className="card__header" style={{ display: 'grid', gap: 10 }}>
            <span style={block(18, '35%')} />
            <span style={block(12, '55%')} />
          </div>
          <div className="card__body" style={{ display: 'grid', gap: 12 }}>
            {[0, 1, 2, 3].map((i) => (
              <span key={i} style={block(48)} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
